import { useCallback, useRef, useEffect } from 'react';
import { getApiBaseUrl } from '../../../utils/apiConfig';

const FLUSH_INTERVAL_MS = 5000;
const FLUSH_THRESHOLD = 50;
const ACTION_DRAG_DROP = 'drag_drop_ordem_servico';

/**
 * Hook para buffer local de logs de auditoria (ex.: drag-and-drop no board).
 * Envia em lote a cada FLUSH_INTERVAL_MS ou ao atingir FLUSH_THRESHOLD itens.
 * @returns {{ logDragDrop: function, flushLogs: function }}
 */
export function useAuditLog() {
  const bufferRef = useRef([]);
  const timerRef = useRef(null);

  const flushLogs = useCallback(async () => {
    if (bufferRef.current.length === 0) return;
    const logsToSend = [...bufferRef.current];
    bufferRef.current = [];
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
    if (!token) return;
    try {
      await fetch(`${getApiBaseUrl()}/api/audit/batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ logs: logsToSend }),
      });
    } catch (_err) {
      bufferRef.current = [...logsToSend, ...bufferRef.current];
    }
  }, []);

  const logDragDrop = useCallback(
    (eventData) => {
      if (!eventData || typeof eventData !== 'object') return;
      const logEntry = {
        timestamp: new Date().toISOString(),
        userId: eventData.userId,
        userName: eventData.userName ?? null,
        tenantId: eventData.tenantId,
        action: eventData.action || ACTION_DRAG_DROP,
        data: {
          ordemServicoId: eventData.ordemId ?? eventData.ordemServicoId,
          origem: eventData.origem,
          destino: eventData.destino,
          posicaoOrigem: eventData.posicaoOrigem,
          posicaoDestino: eventData.posicaoDestino,
          tecnicoCurrent: eventData.tecnicoCurrent,
          tecnicoNovo: eventData.tecnicoNovo,
          sessaoOrigem: eventData.sessaoOrigem,
          sessaoDestino: eventData.sessaoDestino,
          sessionId: typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('sessionId') : null,
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
          ...(typeof eventData.data === 'object' && eventData.data !== null ? eventData.data : {}),
        },
      };
      bufferRef.current.push(logEntry);
      if (bufferRef.current.length >= FLUSH_THRESHOLD) {
        flushLogs();
      } else {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(flushLogs, FLUSH_INTERVAL_MS);
      }
    },
    [flushLogs]
  );

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (bufferRef.current.length > 0) {
        const logsToSend = [...bufferRef.current];
        bufferRef.current = [];
        const token = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
        if (token) {
          fetch(`${getApiBaseUrl()}/api/audit/batch`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ logs: logsToSend }),
          }).catch(() => {});
        }
      }
    };
  }, []);

  return { logDragDrop, flushLogs };
}
